import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Link2, Brain, Sparkles, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import { phCapture } from "@/lib/posthog";

const Index = () => {
  useEffect(() => { phCapture("landing_viewed", { route: "/" }); }, []);
  const ctaLink = "/generate";

  return (
    <Layout>
      <div className="dark-page">
        {/* Hero */}
        <section className="px-4 pt-16 pb-12 md:pt-28 md:pb-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6 text-center">
            <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight md:text-6xl">
              Turn buyer chatter into{" "}
              <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
                conversion copy.
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-slate-200/90 md:text-base">
              Paste keywords → we scan Reddit, reviews &amp; web conversations → you get
              pain themes, objections, and paste-ready copy + a PDF.
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to={ctaLink}
                className="btn-gradient inline-flex w-full items-center justify-center gap-2 rounded-lg px-7 py-3 text-sm font-semibold transition sm:w-auto"
              >
                Generate a report <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex w-full items-center justify-center rounded-lg border border-white/10 px-7 py-3 text-sm font-semibold text-slate-400 transition hover:border-white/20 hover:text-white sm:w-auto"
              >
                See pricing
              </Link>
            </div>

            <p className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
              Evidence-labeled sources. No invented claims.
            </p>
          </div>
        </section>

        {/* Example Output */}
        <section className="px-4 pb-16 md:pb-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="mx-auto max-w-2xl card-surface !p-0 overflow-hidden">
              <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="ml-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Example Output
                </span>
              </div>
              <div className="space-y-4 p-5 text-sm leading-relaxed md:p-6">
                <div className="rounded-xl dark-surface-alt border p-4">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-blue-400">
                    Reddit — r/homegym
                  </p>
                  <p className="text-sm leading-7 text-slate-200/90 italic">
                    "I bought three resistance bands and they all snapped within a month.
                    Total waste of money."
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-teal-500/15 px-3 py-1 text-xs font-medium text-teal-300">
                    Pain: Durability concerns
                  </span>
                  <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-300">
                    Objection: "Not worth the price"
                  </span>
                </div>
              <div className="rounded-xl dark-surface-alt border p-4">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                    Suggested copy angles (3 variations)
                  </p>
                  <ul className="space-y-2 text-sm leading-7 text-slate-200/90">
                    <li>"Bands keep snapping after a few weeks — ours are rated to 2,000+ reps so you never waste money on replacements."</li>
                    <li>"Buyers are tired of cheap bands that break — we reinforced every joint, meaning yours last the full programme."</li>
                    <li>"Durability is the #1 complaint in home-gym bands — we stress-test each set so it performs rep after rep."</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="px-4 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="mb-10 text-center text-xl font-bold md:text-2xl">
              How it works
            </h2>
            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  icon: Link2,
                  color: "text-blue-400 bg-blue-500/15",
                  title: "Paste keywords & sources",
                  desc: "Enter keywords and select evidence sources — Reddit, web pages, YouTube comments.",
                },
                {
                  icon: Brain,
                  color: "text-teal-400 bg-teal-500/15",
                  title: "AI scans real conversations",
                  desc: "We pull buyer language, pain points, and objections from actual discussions.",
                },
                {
                  icon: Sparkles,
                  color: "text-emerald-400 bg-emerald-500/15",
                  title: "Download your copy pack",
                  desc: "Get a client-ready PDF with themes, angles, bullets, and FAQs you can paste immediately.",
                },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="card-surface text-center md:text-left">
                  <div
                    className={`mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${color} md:mx-0`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1.5 text-base font-semibold">{title}</h3>
                  <p className="text-sm leading-7 text-slate-200/90">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className="px-4 pb-16 md:pb-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="mb-8 text-center text-xl font-bold md:text-2xl">
              What you get
            </h2>
            <div className="mx-auto max-w-2xl space-y-4">
              {[
                {
                  title: "Pain Point Mapping",
                  desc: "Buyer frustrations pulled from real conversations — not guesswork.",
                },
                {
                  title: "Sales Page Builder (Shopify-ready)",
                  desc: "Auto-assembled hero, benefits, FAQ, and CTA — paste straight into your store.",
                },
                {
                  title: "Improve My Listing / Page",
                  desc: "Paste your current copy, get an improved version with bullet-by-bullet reasoning.",
                },
                {
                  title: "Evidence-Labeled Sources",
                  desc: "Every insight tagged with its origin — user voice, editorial, or review.",
                },
                {
                  title: "PDF Export + Copy Buttons",
                  desc: "Download the full report or copy any section with one click.",
                },
                {
                  title: "Objection Handling & FAQ",
                  desc: "Pushbacks paired with reassurance angles, ready as FAQ or ad copy.",
                },
              ].map(({ title, desc }) => (
                <div
                  key={title}
                  className="card-surface flex gap-4 !p-5"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                  <div>
                    <h3 className="font-semibold">{title}</h3>
                    <p className="mt-0.5 text-sm leading-7 text-slate-200/90">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Footer */}
        <section className="px-4 py-16 text-center md:py-24">
          <h2 className="text-xl font-bold md:text-2xl">Ready to sift?</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-200/90">
            Generate your first evidence-backed copy pack in under five minutes.
          </p>
          <Link
            to={ctaLink}
            className="btn-gradient mt-8 inline-flex items-center gap-2 rounded-lg px-8 py-3.5 text-sm font-semibold transition"
          >
            Generate a report <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default Index;
